import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { useAuth } from '../components/auth/AuthProvider';
import { StaticPageLayout } from '../layouts/static-page-layout';
import { AceRenderer } from '@/components/ui/ace-renderer';
import { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { 
  Brain, 
  Target, 
  BookOpen, 
  Users, 
  Zap, 
  TrendingUp,
  ChartBar,
  Award,
  Lightbulb,
  Clock,
  Calendar,
  ArrowRight,
  CheckCircle,
  Star,
  Play,
  Calculator,
  Microscope,
  Scroll,
  Sparkles,
  Code,
  Cpu,
  Rocket,
  X,
  MessageSquare,
  History
} from 'lucide-react';

export function HomePage() {
  const { user, session } = useAuth();
  
  // Check if user is truly authenticated (has both user and valid session)
  const isAuthenticated = user && session;
  
  // Set page title
  useEffect(() => {
    document.title = 'AceHorizon - AI-Powered Educational Platform';
  }, []);
  
  // Carousel state management
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const heroFeatures = [
    {
      icon: Brain,
      title: "AI-Powered Concept Mapping",
      description: (
        <>
          <em>Textbooks reimagined:</em> Our AI-powered engine dissects complex subjects into core concepts and maps their real-world relationships—automatically.
        </>
      ),
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
    },
    {
      icon: MessageSquare,
      title: "AI Companion",
      description: (
        <>
          <em>Your textbook comes alive:</em> Our AI understands your textbook. Ask "Explain steps of photosynthesis in plants." or "Summarize characters of English chapter 5 in table format." - get instant, personalized responses with conversation history saved for exam prep.
        </>
      ),
      color: "bg-emerald-500",
      gradient: "from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20"
    },
    {
      icon: Target,
      title: "Knowledge Beacons",
      description: (
        <>
          <em>See the invisible connections:</em> Watch abstract concepts transform into an interactive galaxy of ideas, where physics links to history, and math illuminates music.
        </>
      ),
      color: "bg-green-500",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20"
    },
    {
      icon: Users,
      title: "Comprehension Assessment",
      description: (
        <>
          <em>More than quizzes:</em> Our AI analyzes your unique understanding through concept explanations and adaptive questioning, pinpointing knowledge gaps at a granular level.
        </>
      ),
      color: "bg-orange-500",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20"
    },
    {
      icon: CheckCircle,
      title: "Smart Syllabus Tracking",
      description: (
        <>
          <em>Never miss what matters:</em> Track your complete syllabus with weighted topics, upcoming tests, and coverage analysis. Know exactly what to study and when.
        </>
      ),
      color: "bg-indigo-500",
      gradient: "from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20"
    }
  ];

  // Cell and Microscope D3 Network Component
  const CellMicroscopeNetwork = () => {
    const svgRef = useRef<SVGSVGElement>(null);

    const graphData = {
      nodes: [
        { id: 'cell', name: 'The Cell', type: 'Concept', color: '#8b5cf6', val: 19 },
        { id: 'microscope', name: 'Microscope', type: 'Concept', color: '#10b981', val: 18 },
        { id: 'biology', name: 'Biology Revolution', type: 'Event', color: '#f59e0b', val: 18 },
        { id: 'medicine', name: 'Medical Advances', type: 'Example', color: '#3b82f6', val: 18 }
      ],
      links: [
        { source: 'microscope', target: 'cell', label: 'revealed' },
        { source: 'cell', target: 'biology', label: 'revolutionized' },
        { source: 'cell', target: 'medicine', label: 'advanced' },
        { source: 'microscope', target: 'biology', label: 'enabled' }
      ]
    };

    useEffect(() => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      const width = 600;
      const height = 400;

      // Clear any existing content
      svg.selectAll("*").remove();

      // Create arrow markers - adjusted for larger nodes
      svg.append("defs").selectAll("marker")
        .data(["compact-arrow"])
        .enter().append("marker")
        .attr("id", "compact-arrow")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 60) // Increased for larger circles
        .attr("refY", 0)
        .attr("markerWidth", 6)
        .attr("markerHeight", 6)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", "#64748b");

      // Create simulation - optimized for larger canvas with better spacing
      const simulation = d3.forceSimulation(graphData.nodes as any)
        .force("link", d3.forceLink(graphData.links as any)
          .id((d: any) => d.id)
          .distance(280) // Increased for larger canvas
        )
        .force("charge", d3.forceManyBody().strength(-600)) // Stronger repulsion for larger space
        .force("center", d3.forceCenter(width / 2, height / 2))
        .force("collision", d3.forceCollide().radius(60)) // Larger collision radius
        .force("x", d3.forceX(width / 2).strength(0.1))
        .force("y", d3.forceY(height / 2).strength(0.1));

      // Create links - increased size for better visibility
      const link = svg.append("g")
        .attr("class", "links")
        .selectAll("line")
        .data(graphData.links)
        .enter().append("line")
        .attr("stroke", "#64748b")
        .attr("stroke-width", 4) // Increased from 2 for better visibility
        .attr("marker-end", "url(#compact-arrow)");

      // Create link labels - larger and more visible
      const linkLabel = svg.append("g")
        .attr("class", "link-labels")
        .selectAll("text")
        .data(graphData.links)
        .enter().append("text")
        .attr("class", "link-label")
        .attr("text-anchor", "middle")
        .attr("font-size", "12px") // Increased for better readability
        .attr("fill", "#4b5563") // Slightly darker for better contrast
        .attr("dy", -15) // Moved slightly up for thicker links
        .style("font-weight", "600") // Increased weight for better visibility
        .text((d: any) => d.label);

      // Create node groups - same structure as knowledge beacons
      const node = svg.append("g")
        .attr("class", "nodes")
        .selectAll("g")
        .data(graphData.nodes)
        .enter().append("g")
        .attr("class", "node");

      node.append("circle")
        .attr("r", (d: any) => Math.max(d.val * 3.5, 50)) // Significantly larger nodes
        .attr("fill", (d: any) => d.color)
        .attr("stroke", "#fff")
        .attr("stroke-width", 3)
        .style("filter", "drop-shadow(2px 2px 4px rgba(0,0,0,0.2))");

      // Add main title labels inside nodes - modified to put second word on new line
      node.each(function(d: any) {
        const nodeGroup = d3.select(this);
        const words = d.name.split(/\s+/);
        const lines: string[] = [];

        // Put first word on first line, remaining words on subsequent lines
        if (words.length > 0) {
          lines.push(words[0]); // First word on first line
          
          if (words.length > 1) {
            // Put remaining words on new lines, but group them if they're short
            let currentLine = "";
            for (let i = 1; i < words.length; i++) {
              if (currentLine === "") {
                currentLine = words[i];
              } else if ((currentLine + " " + words[i]).length <= 12) {
                currentLine += " " + words[i];
              } else {
                lines.push(currentLine);
                currentLine = words[i];
              }
            }
            if (currentLine) lines.push(currentLine);
          }
        }

        // Add each line as a separate tspan - larger text for bigger nodes
        const textElement = nodeGroup.append("text")
          .attr("text-anchor", "middle")
          .attr("font-size", "14px") // Increased for larger nodes
          .attr("font-weight", "bold")
          .attr("fill", "#ffffff")
          .style("text-shadow", "2px 2px 4px rgba(0,0,0,0.9)")
          .style("pointer-events", "none");

        lines.forEach((line: string, i: number) => {
          textElement.append("tspan")
            .attr("x", 0)
            .attr("dy", i === 0 ? `${-0.4 * (lines.length - 1)}em` : "1.1em")
            .text(line);
        });
      });

      // Add type labels inside nodes - adjusted for larger nodes
      node.append("text")
        .attr("dx", 0)
        .attr("dy", "2.2em") // Adjusted for larger nodes
        .attr("text-anchor", "middle")
        .attr("font-size", "11px") // Increased for larger nodes
        .attr("font-weight", "500")
        .attr("fill", "#fbbf24") // Same golden yellow as knowledge beacons
        .style("text-shadow", "1px 1px 3px rgba(0,0,0,0.8)")
        .style("pointer-events", "none")
        .style("opacity", 1)
        .text((d: any) => d.type);

      // Update positions on simulation tick - adjusted for larger nodes
      simulation.on("tick", () => {
        const nodeRadius = 60; // Increased for larger nodes
        
        graphData.nodes.forEach((d: any) => {
          d.x = Math.max(nodeRadius, Math.min(width - nodeRadius, d.x));
          d.y = Math.max(nodeRadius, Math.min(height - nodeRadius, d.y));
        });

        link
          .attr("x1", (d: any) => d.source.x)
          .attr("y1", (d: any) => d.source.y)
          .attr("x2", (d: any) => d.target.x)
          .attr("y2", (d: any) => d.target.y);

        linkLabel
          .attr("x", (d: any) => (d.source.x + d.target.x) / 2)
          .attr("y", (d: any) => (d.source.y + d.target.y) / 2)
          .attr("transform", (d: any) => {
            const dx = d.target.x - d.source.x;
            const dy = d.target.y - d.source.y;
            const angle = Math.atan2(dy, dx) * 180 / Math.PI;
            const x = (d.source.x + d.target.x) / 2;
            const y = (d.source.y + d.target.y) / 2;
            
            // Keep text readable by flipping it if it would be upside down
            const adjustedAngle = (angle > 90 || angle < -90) ? angle + 180 : angle;
            
            return `rotate(${adjustedAngle}, ${x}, ${y})`;
          });

        node.attr("transform", (d: any) => `translate(${d.x},${d.y})`);
      });

      // Stop simulation after settling - same as knowledge beacons
      setTimeout(() => {
        simulation.stop();
      }, 3000);

      // Cleanup
      return () => {
        simulation.stop();
      };
    }, []);

    return (
      <svg 
        ref={svgRef}
        width={600}
        height={400}
        className="w-full h-full border rounded-lg bg-white dark:bg-gray-800"
      />
    );
  }; 

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center py-5 sm:py-12">
          <div className="max-w-4xl mx-auto px-2 sm:px-4">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
              Welcome to Ace Horizon
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              Experience the future of education with our AI-powered learning platform designed to 
              <span className="text-primary font-semibold"> personalize your journey</span> and 
              <span className="text-primary font-semibold"> accelerate your success</span>. 
              Currently serving Secondary students of India.
            </p>
          </div>
        </div>

        {/* Hero Carousel - Full Width */}
        <div className="mb-12">
          {/* Carousel header with instructions */}
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold bg-gradient-to-r from-slate-700 via-blue-700 to-slate-700 dark:from-slate-300 dark:via-blue-300 dark:to-slate-300 bg-clip-text text-transparent mb-2">
              Discover Our Key Features
            </h2>
          </div>
          
          <Carousel 
            className="w-full relative" 
            setApi={setApi}
            opts={{ 
              align: "start", 
              loop: true, 
              dragFree: true,
              containScroll: "trimSnaps"
            }}
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {heroFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                    <div className="p-1 h-full">
                      <Card className={`border-0 bg-gradient-to-br ${feature.gradient} transition-all duration-300 relative hover:shadow-lg hover:scale-105 cursor-pointer group h-full min-h-[20rem] sm:min-h-[22rem] flex flex-col`}>
                        {/* Mobile carousel indicator */}
                        <div className="absolute top-3 right-3 sm:hidden">
                          <span className="text-xs bg-white/90 dark:bg-gray-800/90 px-2 py-1 rounded-full text-gray-600 dark:text-gray-300 font-medium">
                            {index + 1}/{heroFeatures.length}
                          </span>
                        </div>
                        <CardContent className="flex flex-col items-center justify-center p-4 sm:p-6 flex-1 text-center">
                          <div className="flex flex-col items-center justify-center h-full space-y-3 sm:space-y-4">
                            <div className={`w-12 sm:w-16 h-12 sm:h-16 ${feature.color} rounded-full flex items-center justify-center animate-pulse group-hover:animate-bounce transition-all flex-shrink-0`}>
                              <IconComponent className="h-6 sm:h-8 w-6 sm:w-8 text-white" />
                            </div>
                            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white text-center flex-shrink-0">
                              {feature.title}
                            </h3>
                            <div className="flex-1 flex items-center overflow-hidden">
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 text-center leading-relaxed overflow-hidden [display:-webkit-box] [-webkit-line-clamp:6] sm:[-webkit-line-clamp:8] [-webkit-box-orient:vertical]">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            
            {/* Always visible arrows with better styling */}
            <CarouselPrevious className="absolute -left-4 sm:-left-12 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 bg-white/95 dark:bg-gray-800/95 border-2 border-gray-200 dark:border-gray-600 shadow-lg hover:bg-white dark:hover:bg-gray-700 hover:scale-110 transition-all duration-200 z-10" />
            <CarouselNext className="absolute -right-4 sm:-right-12 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 bg-white/95 dark:bg-gray-800/95 border-2 border-gray-200 dark:border-gray-600 shadow-lg hover:bg-white dark:hover:bg-gray-700 hover:scale-110 transition-all duration-200 z-10" />
          </Carousel>
          
          {/* Mobile dot indicators and swipe hint */}
          <div className="flex justify-center items-center mt-6 gap-4 sm:hidden">
            <div className="flex gap-2">
              {heroFeatures.map((_, index) => (
                <button
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    current === index + 1
                      ? 'bg-blue-500 scale-125'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  }`}
                  onClick={() => api?.scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Swipe for more
            </span>
          </div>
        </div>

        {/* Interactive AI Tutor Demo Section */}
        <div className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/10 dark:via-teal-900/10 dark:to-cyan-900/10 rounded-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent mb-6">
              Your Textbook Comes Alive
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Our AI understands your textbook. Ask any question and get instant, 
              personalized responses in your preferred format with conversation history for exam prep.
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            {/* Interactive Chat Demo */}
            <div className="order-2 lg:order-1">
              <Card className="p-6 shadow-2xl border-0 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">AI Tutor</h3>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-600 dark:text-green-400">Online</span>
                  </div>
                </div>

                <div className="space-y-4 mb-4 max-h-80 overflow-y-auto">
                  {/* Student Question */}
                  <div className="flex justify-end">
                    <div className="bg-emerald-500 text-white rounded-2xl rounded-tr-md px-4 py-3 max-w-xs">
                      <p className="text-sm">Explain the steps of photosynthesis in plants.</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-md px-4 py-3 max-w-md">
                      <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">🌱 Photosynthesis Steps:</p>
                      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                        <li>• Absorption of light energy by chlorophyll</li>
                        <li>• Conversion of light energy to chemical energy</li>
                        <li>• Splitting of water molecules into hydrogen and oxygen</li>
                        <li>• Reduction of carbon dioxide to carbohydrates</li>
                        <li>• Storage of excess carbohydrates as starch</li>
                      </ul>
                    </div>
                  </div>

                  {/* Follow-up Question */}
                  <div className="flex justify-end">
                    <div className="bg-emerald-500 text-white rounded-2xl rounded-tr-md px-4 py-3 max-w-xs">
                      <p className="text-sm">What's the difference between HCF and LCM using Euclid's division algorithm?</p>
                    </div>
                  </div>

                  {/* AI Math Response */}
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-md px-4 py-3 max-w-lg">
                      <p className="text-sm text-gray-900 dark:text-white font-medium mb-3">📊 Math Chapter 1: Real Numbers</p>
                      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                        <div><strong>HCF (Highest Common Factor):</strong></div>
                        <div>• Largest number that divides both numbers</div>
                        <div>• Found using Euclid's algorithm: a = bq + r</div>
                        <div>• Example: HCF(455, 42) = 7</div>
                        <div className="mt-2"><strong>LCM (Lowest Common Multiple):</strong></div>
                        <div>• Smallest multiple common to both numbers</div>
                        <div>• Formula: LCM * HCF = Product of numbers</div>
                      </div>
                    </div>
                  </div>

                  {/* Third Question */}
                  <div className="flex justify-end">
                    <div className="bg-emerald-500 text-white rounded-2xl rounded-tr-md px-4 py-3 max-w-xs">
                      <p className="text-sm">Explain how French Revolution spread nationalism in Europe.</p>
                    </div>
                  </div>

                  {/* AI History Response */}
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-md px-4 py-3 max-w-lg">
                      <p className="text-sm text-gray-900 dark:text-white font-medium mb-2">� History Chapter 1: Rise of Nationalism</p>
                      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                        <li>• Transferred sovereignty from monarchy to French citizens</li>
                        <li>• Created collective identity through la patrie and le citoyen</li>
                        <li>• Introduced tricolor flag and National Assembly</li>
                        <li>• Napoleon's armies carried nationalist ideas across Europe</li>
                        <li>• Established Civil Code and abolished feudalism in conquered territories</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <input 
                    type="text" 
                    placeholder="Ask anything about your textbook..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white"
                    disabled
                  />
                  <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" disabled>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            </div>

            {/* Features List */}
            <div className="order-1 lg:order-2 space-y-8">
              <div className="space-y-6">
                {[
                  {
                    icon: MessageSquare,
                    title: "Ask Anything",
                    description: "Natural language questions about any topic in your textbooks",
                    example: '"How does Euclid\'s algorithm work with examples?"'
                  },
                  {
                    icon: Target,
                    title: "Multiple Formats",
                    description: "Get answers as bullet points, tables, summaries, or detailed explanations",
                    example: "Steps, comparisons, chapter summaries"
                  },
                  {
                    icon: History,
                    title: "Conversation History",
                    description: "All your questions and answers saved automatically for exam preparation",
                    example: "Review Math formulas, Science concepts anytime"
                  },
                  {
                    icon: BookOpen,
                    title: "Complete Textbook Coverage",
                    description: "Every chapter from Math, Science, English, History, Geography & Political Science",
                    example: "Real Numbers, Life Processes, Nationalism in Europe"
                  }
                ].map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4 group">
                      <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                          {feature.description}
                        </p>
                        <div className="inline-flex items-center space-x-1 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                          <span>Example:</span>
                          <span className="italic">{feature.example}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <Zap className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold">Powered by Advanced AI</h3>
                </div>
                <p className="text-emerald-50 text-sm leading-relaxed">
                  Our AI understands and processes your textbook, understanding context, 
                  relationships between topics, and can provide explanations tailored to your grade level.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Learning Paths */}
        <div className="py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent mb-6">
              Explore Knowledge Beacons
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto mb-8">
              Our AI categorizes textbook content into four interconnected entity types: <strong>Concepts, Events, Figures, and Examples</strong>. 
              See how "The Cell" (concept) connects to "Microscope" (concept) and "Biology Revolution" (event) across Science, History, and Technology - building understanding through cross-subject relationships, not memorization.
            </p>

            {/* D3 Entity Network Visualization with Beacon Spotlight - 60:40 Layout */}
            <div className="max-w-6xl mx-auto mb-8 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl p-6 relative border border-gray-200 dark:border-gray-700">
              <div className="grid lg:grid-cols-5 gap-6">
                {/* Knowledge Graph - 60% space (3 columns) */}
                <div className="lg:col-span-3 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full animate-pulse"></div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                      Knowledge Network
                    </h3>
                  </div>
                  <div className="flex justify-center">
                    <CellMicroscopeNetwork />
                  </div>
                </div>

                {/* Beacon Spotlight - 40% space (2 columns) */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                      Beacon Spotlight
                    </h3>
                  </div>
                  
                  {/* Beacon Header */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white shadow-lg">
                        <Microscope className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-gray-900 dark:text-white">
                            The Cell
                          </h4>
                          <Badge variant="secondary" className="text-xs">
                            Concept
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Biology
                          </Badge>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                          The basic structural and functional unit of all known living organisms, whose intricate structure could not be observed with the naked eye.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Key Relationships */}
                  <div className="space-y-3">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border-l-4 border-blue-500">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                          <Microscope className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                              Scientific Discovery
                            </h5>
                            <Badge variant="outline" className="text-xs">
                              Technological Enablement
                            </Badge>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">
                            The cell's discovery was made possible by the invention of the microscope, a scientific instrument that greatly magnifies small objects.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border-l-4 border-emerald-500">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                          <History className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                              Historical Impact
                            </h5>
                            <Badge variant="outline" className="text-xs">
                              Cross-Subject Connection
                            </Badge>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed">
                            This discovery revolutionized Biology and Medicine, demonstrating how technology drives scientific progress.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="px-8 bg-gradient-to-r from-emerald-500 to-orange-500 hover:from-emerald-600 hover:to-orange-600">
              <Link to="/knowledge-beacons">
                Discover Knowledge Beacons
                <Sparkles className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Sparks Section */}
        <div className="py-20  from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-3xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 dark:from-amber-400 dark:via-orange-400 dark:to-red-400 bg-clip-text text-transparent mb-6">
              Explore Academic Subjects with Sparks
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Go beyond textbooks. Discover how subjects evolved, their specializations, career paths, 
              and the brilliant minds who shaped them. From ancient philosophy to quantum computing.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { 
                icon: Calculator, 
                title: "STEM Fields", 
                description: "Mathematics, Physics, Computer Science, Engineering - discover how they interconnect and evolve.",
                examples: ["Quantum Physics", "AI & Machine Learning", "Bioengineering"],
                color: "bg-blue-500"
              },
              { 
                icon: BookOpen, 
                title: "Humanities", 
                description: "Literature, History, Philosophy, Art - explore the cultural foundations of human knowledge.",
                examples: ["Renaissance Art", "Ancient Philosophy", "Modern Literature"],
                color: "bg-green-500"
              },
              { 
                icon: Users, 
                title: "Social Sciences", 
                description: "Psychology, Economics, Political Science - understand human behavior and society.",
                examples: ["Behavioral Economics", "Social Psychology", "Political Theory"],
                color: "bg-purple-500"
              },
              { 
                icon: Lightbulb, 
                title: "Applied Sciences", 
                description: "Medicine, Business, Law, Environmental Science - see knowledge in action.",
                examples: ["Biomedical Engineering", "Climate Science", "Corporate Strategy"],
                color: "bg-orange-500"
              }
            ].map((category, index) => {
              const IconComponent = category.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white dark:bg-gray-800">
                  <CardHeader className="pb-4">
                    <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg font-bold group-hover:text-primary transition-colors">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">Examples:</p>
                      <div className="flex flex-wrap gap-1">
                        {category.examples.map((example, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center space-y-6">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              {[
                { icon: Star, title: "Historical Evolution", description: "See how subjects developed over centuries" },
                { icon: Target, title: "Career Pathways", description: "Understand job markets and opportunities" },
                { icon: Award, title: "Notable Personalities", description: "Meet the pioneers who shaped each field" }
              ].map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                  </div>
                );
              })}
            </div>
            
            <Button asChild size="lg" className="px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
              <Link to="/discover-sparks">
                Explore Sparks
                <Sparkles className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Comprehension Assessment Section */}
        <div className="py-20 from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 rounded-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent mb-6">
              Advanced Comprehension Assessment
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
              Our AI based assessment system works <em className="text-primary font-semibold">behind the scenes</em> to understand 
              your learning patterns and <em className="text-primary font-semibold">personalize every explanation</em> you receive.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { 
                icon: Brain, 
                title: "Deep Understanding Analysis", 
                description: "Our AI analyzes not just your answers, but your reasoning patterns and thinking methodology.",
                color: "bg-blue-500"
              },
              { 
                icon: Target, 
                title: "20 Carefully Crafted Questions", 
                description: "Each assessment contains exactly 20 questions randomly selected from a pool of thousands of questions designed to evaluate different aspects of your comprehension.",
                color: "bg-green-500"
              },
              { 
                icon: Zap, 
                title: "Intelligent Content Tailoring", 
                description: "AceHorizon uses your results to customize every concept explanation to match your comprehension level perfectly.",
                color: "bg-purple-500"
              },
              {
                icon: CheckCircle,
                title: "Stress-Free Assessment",
                description: "Results are never shown to you, eliminating test anxiety while providing valuable insights to the system.",
                color: "bg-indigo-500"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white dark:bg-gray-800">
                  <CardHeader className="text-center pb-4">
                    <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg font-bold group-hover:text-primary transition-colors">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center text-sm leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center space-y-6">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              {[
                { icon: Calendar, title: "Quarterly Schedule", description: "Four assessments throughout the academic year" },
                { icon: Users, title: "Personalized Learning", description: "Every explanation tailored to your comprehension level" },
                { icon: Award, title: "Privacy First", description: "Results used only to improve your learning experience" }
              ].map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
            
            <Button asChild size="lg" className="px-8 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600">
              <Link to="/comprehension-assessment">
                Learn About Our Assessment System
                <Brain className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent mb-6">
              Ready to Transform Your Learning Journey?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
              Join thousands of learners who have already discovered the power of AI-enhanced education with Ace Horizon
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              {isAuthenticated ? (
                <Button asChild size="lg" className="px-12 py-4 text-lg">
                  <Link to="/dashboard">
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild size="lg" className="px-12 py-4 text-lg">
                    <Link to="/login">
                      Start Learnig Today
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Free to get started</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </div>
    </StaticPageLayout>
  );
}
